import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "../../../integrations/supabase/client";
import { exchangeSocialCode } from "../../../server/social";
import { Button } from "../../../components/ui/button";

export const Route = createFileRoute("/auth/tiktok/callback")({
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === "string" ? search.code : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
    error_description:
      typeof search.error_description === "string" ? search.error_description : undefined,
  }),
  component: TikTokCallbackPage,
});

function TikTokCallbackPage() {
  const navigate = useNavigate();
  const { code, state, error, error_description } = Route.useSearch();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Connecting your TikTok account...");
  // OAuth codes are single-use — guard against StrictMode double-mount /
  // remount exchanging the same code twice and flashing an error after success.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    if (typeof window === "undefined" || !supabase) return;
    const sb = supabase;

    const finish = async () => {
      if (error) {
        setStatus("error");
        setMessage(error_description ?? "TikTok authorization was denied or failed.");
        return;
      }
      if (!code || !state) {
        setStatus("error");
        setMessage("Missing authorization response from TikTok. Please try connecting again.");
        return;
      }

      const {
        data: { session },
      } = await sb.auth.getSession();
      if (!session) {
        setStatus("error");
        setMessage("You need to be signed in to finish connecting TikTok.");
        return;
      }

      const res = await exchangeSocialCode({
        data: {
          accessToken: session.access_token,
          provider: "tiktok",
          code,
          state,
          redirectUri: `${window.location.origin}/auth/tiktok/callback`,
        },
      });

      if (res.error) {
        setStatus("error");
        setMessage(res.error);
        return;
      }

      setStatus("success");
      setMessage("TikTok connected! Redirecting to Content Studio...");
      setTimeout(() => navigate({ to: "/content-management" }), 1500);
    };

    finish();
  }, [code, state, error, error_description, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          TikTok Connection
        </h1>

        {status === "loading" && (
          <div className="space-y-4">
            <div className="text-4xl animate-spin">⏳</div>
            <p className="text-muted-foreground">{message}</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <div className="text-4xl">✅</div>
            <p className="text-green-600 dark:text-green-400">{message}</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <div className="text-4xl">❌</div>
            <p className="text-destructive">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/content-management">Back to Content Studio</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
